/**
 * <Requests> React Component
 */

// #region Imports

import React, { useState, useEffect } from 'react';
import RequestItem from './RequestItem';
import RequestsContainer from './RequestsContainer';
import { NewClientRequest, type ClientRequest } from './ClientRequestService';
import { useClientRequest } from './useClientRequest';
import { type NotifyFunction } from './NotificationsService';

// #endregion Imports

interface RequestsProps {
  /** The client ID for which to display requests */
  clientId: string;

  /** Function for displaying notifications */
  notify: NotifyFunction;
}

/**
 * Requests component for displaying and managing client requests.
 * Converts the AngularJS template to React.
 * @param props - Props for the Requests component
 * @returns JSX.Element representing the Requests component
 */
const Requests: React.FC<RequestsProps> = ({ clientId, notify }) => {
  const [activeRequests, setActiveRequests] = useState<ClientRequest[]>([]);
  const [completedRequests, setCompletedRequests] = useState<ClientRequest[]>([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const { getRequestList, createRequest } = useClientRequest(clientId);
  /**
   * Shows a notification if the service is available
   */
  const showNotification: NotifyFunction = (message, options) => {
    notify(message, options || { length: 3, type: 'success' });
  };

  /**
   * Fetches and updates the request lists (active and completed)
   */
  const updateRequestList = async () => {
    try {
      const { requests: allRequests } = await getRequestList();
      const active = allRequests.filter(request => request.status !== 'COMPLETED');
      const completed = allRequests.filter(request => request.status === 'COMPLETED');

      setActiveRequests(active);
      setCompletedRequests(completed);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  useEffect(() => {
    updateRequestList();
  }, [clientId]);

  /**
   * Toggles the visibility of completed requests
   */
  const toggleShowCompleted = () => {
    setShowCompleted(!showCompleted);
  };

  /**
   * Opens the new request form
   */
  const openNewRequest = () => {
    setNewRequestOpen(true);
  };

  /**
   * Closes the new request form
   */
  const closeNewRequest = () => {
    setNewRequestOpen(false);
  };

  /**
   * Saves a new request and updates the request list
   * @param newRequest - The new request to save
   */
  const saveNewRequest = async (newRequest: NewClientRequest) => {
    try {
      await createRequest(newRequest);
      closeNewRequest();
      await updateRequestList();
    } catch (error) {
      console.error('Error saving new request:', error);
      throw error;
    }
  };

  return (
    <RequestsContainer>
      <header className="requests_header">
        <div className="requests_header-label">Requests</div>
      </header>

      <div className="requests_list-container">
        <div data-testid="request-list" className="requests_list">
          {activeRequests.map((request, index) => (
            <RequestItem
              key={request.id}
              request={request}
              clientId={clientId}
              onSaveOrComplete={updateRequestList}
              notify={showNotification}
              testIdIndex={index}
            />
          ))}

          {showCompleted && (
            <div data-testid="completed-requests">
              <div className="requests_list-completed-title">Completed</div>

              {completedRequests.map((request, index) => (
                <RequestItem
                  key={request.id}
                  request={request}
                  clientId={clientId}
                  onSaveOrComplete={updateRequestList}
                  notify={showNotification}
                  testIdIndex={index}
                />
              ))}
            </div>
          )}
        </div>

        <div className="requests_new-item">
          {!newRequestOpen && (
            <button
              onClick={openNewRequest}
              data-testid="button-new-request"
              className="requests_button requests_new-request-button"
            >
              <span className="bl-icon bl-icon-plus requests_button-icon"></span>
              <span className="requests_button-text">New request</span>
            </button>
          )}

          {newRequestOpen && (
            <RequestItem
              isNew={true}
              clientId={clientId}
              onSaveOrComplete={updateRequestList}
              onSaveNewRequest={saveNewRequest}
              onCancelNewRequest={closeNewRequest}
              notify={showNotification}
            />
          )}
        </div>
      </div>

      <div className="requests_toggle-complete">
        <button
          onClick={toggleShowCompleted}
          data-testid="button-toggle-completed-requests"
          className="requests_button requests_toggle-button"
        >
          {showCompleted ? 'Hide' : 'Show'} completed requests
        </button>
      </div>
    </RequestsContainer>
  );
};

export default Requests;
